import type { CoordEntry } from "./types";

type CategorySeries = {
  pattern: RegExp;
  chronologicalOrder: number;
  label: (issue: number) => string;
};

// 下にあるシリーズほど新しい時系列として扱います。
const CATEGORY_SERIES: CategorySeries[] = [
  { pattern: /^P0*(\d+)$/i, chronologicalOrder: -3, label: (issue) => `プリマジ${issue}章` },
  { pattern: /^2P0*(\d+)$/i, chronologicalOrder: -2, label: (issue) => `スタジオ${issue}章` },
  { pattern: /^3P0*(\d+)$/i, chronologicalOrder: -1, label: (issue) => `スタジオ${issue + 8}章` },
  { pattern: /^(\d+)$/i, chronologicalOrder: 0, label: (issue) => `${issue}弾` },
  { pattern: /^ring(\d+)$/i, chronologicalOrder: 1, label: (issue) => `リング${issue}弾` },
  { pattern: /^oa(\d+)$/i, chronologicalOrder: 2, label: (issue) => `おねがい${issue}弾` },
];

function categoryInfo(category: string | null | undefined) {
  const value = category?.trim() ?? "";
  for (const series of CATEGORY_SERIES) {
    const match = value.match(series.pattern);
    if (match) return { series, issue: Number(match[1]) };
  }
  return null;
}

export function categoryDisplayName(category: string | null | undefined): string {
  const info = categoryInfo(category);
  if (info) return info.series.label(info.issue);
  return category?.trim() || "-";
}

export function compareCategoriesNewestFirst(
  left: string | null | undefined,
  right: string | null | undefined,
): number {
  const leftInfo = categoryInfo(left);
  const rightInfo = categoryInfo(right);
  if (leftInfo && rightInfo) {
    return rightInfo.series.chronologicalOrder - leftInfo.series.chronologicalOrder
      || rightInfo.issue - leftInfo.issue;
  }
  if (leftInfo) return -1;
  if (rightInfo) return 1;
  return categoryDisplayName(left).localeCompare(categoryDisplayName(right), "ja");
}

export function sortEntriesByNewestCategory(entries: CoordEntry[]): CoordEntry[] {
  return entries
    .map((entry, originalIndex) => ({ entry, originalIndex }))
    .sort((left, right) =>
      compareCategoriesNewestFirst(left.entry.category, right.entry.category)
      || left.originalIndex - right.originalIndex
    )
    .map(({ entry }) => entry);
}

export type CatalogQuickFilter = "latest" | "oa" | "secret" | "primagi";

export function categoryMatchesCatalogQuickFilter(
  category: string,
  filter: CatalogQuickFilter,
  newestCategory: string | null,
): boolean {
  if (filter === "latest") return Boolean(newestCategory) && category === newestCategory;
  if (filter === "oa") return /^oa\d+$/i.test(category);
  if (filter === "secret") return /^(?:\d+|v\d+|ring\d+|apr\d+)$/i.test(category);
  return /^(?:P|2P|3P)0*\d+$/i.test(category);
}

export function categoriesForCatalogQuickFilter(
  categories: Iterable<string>,
  filter: CatalogQuickFilter,
  newestCategory: string | null,
): string[] {
  return [...categories].filter((category) =>
    categoryMatchesCatalogQuickFilter(category, filter, newestCategory)
  );
}

export function latestCategory(entries: CoordEntry[]): string | null {
  return sortEntriesByNewestCategory(entries)[0]?.category ?? null;
}

export function matchesCatalogQuickFilter(
  entry: CoordEntry,
  filter: CatalogQuickFilter,
  newestCategory: string | null,
): boolean {
  const category = entry.category?.trim() ?? "";
  return categoryMatchesCatalogQuickFilter(category, filter, newestCategory)
    || (filter === "primagi" && entry.series === "primagi");
}
